'use client';

import { memo, type CSSProperties } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Text } from '../../../components/styled/text';
import { MarkdownPreview } from '../../markdown-preview';
import { ToolResultRenderer } from './ToolCallsRenderers/ToolResultRenderer';
import { prepareToolResultData } from './ToolCallsRenderers/content-detection';
import { getToolConfig } from './tool-config';
import {
  formatToolInputPayload,
  readTaskDescriptionFromToolInput,
} from './tool-input-utils';
import type { SseSubscribe } from '@terra/shared-sse-contracts';
import type { ToolCallProgressEvent } from '@terra/shared-dto-chat-types';
import type { OnOpenChatObject } from './hitl-types';

const pendingResultStyle: CSSProperties = {
  background:
    'linear-gradient(90deg, var(--colors-bg-muted) 25%, var(--colors-border-neutral-muted) 50%, var(--colors-bg-muted) 75%)',
  backgroundSize: '200% 100%',
};

type ToolCallContentRendererProps = {
  input: unknown;
  output: unknown;
  progressEvents?: ToolCallProgressEvent[];
  toolName?: string;
  hideRequestIfShownInHITL?: boolean;
  toolCallId?: string;
  toolCallIdFallback?: string;
  isStarted?: boolean;
  isPendingResult?: boolean;
  onSSESubscribe?: SseSubscribe;
  onOpenBrowserStreamInMainContent?: (data: { streamingId: string }) => void;
  onOpenObject?: OnOpenChatObject;
};

export const ToolCallContentRenderer = memo(function ToolCallContentRenderer({
  input,
  output,
  progressEvents,
  toolName,
  hideRequestIfShownInHITL,
  toolCallId,
  toolCallIdFallback,
  isStarted,
  isPendingResult,
  onSSESubscribe,
  onOpenBrowserStreamInMainContent,
  onOpenObject,
}: ToolCallContentRendererProps) {
  const t = useTranslations('designSystem.chat.toolCallContentRenderer');
  const config = getToolConfig(toolName);
  const taskDescription = readTaskDescriptionFromToolInput(input);

  const inputDataForTaskDescription =
    config.showsTaskDescription && taskDescription
      ? prepareToolResultData({
          content: taskDescription,
          toolName: 'markdown',
        })
      : null;

  const inputData =
    input && !config.showsTaskDescription
      ? prepareToolResultData({ content: input, toolName })
      : null;

  const outputData = output
    ? prepareToolResultData({ content: output, toolName, input })
    : null;

  // Hide input if config says to or if HITL already showed it
  const shouldShowInput =
    !!input && !config.hideInput && !(hideRequestIfShownInHITL ?? false);

  const formattedPayload = shouldShowInput
    ? formatToolInputPayload({
        formatPayload: config.formatPayload,
        input,
      })
    : null;

  const effectiveToolCallId = toolCallId ?? toolCallIdFallback;
  const shouldShowPendingResult =
    !outputData &&
    !config.isStreaming &&
    !(hideRequestIfShownInHITL ?? false) &&
    (isPendingResult ?? isStarted ?? false);

  const streamingContent =
    config.isStreaming && effectiveToolCallId && config.renderStreamingContent
      ? config.renderStreamingContent({
          toolCallId: effectiveToolCallId,
          onSSESubscribe,
          onOpenBrowserStreamInMainContent,
          progressEvents,
          isRunning: isStarted,
        })
      : null;

  return (
    <>
      {shouldShowInput ? (
        <VStack alignItems="start" gap={2} width="100%">
          {formattedPayload ? (
            <MarkdownPreview source={formattedPayload} fontSize={12} compact />
          ) : inputDataForTaskDescription ? (
            <ToolResultRenderer
              data={inputDataForTaskDescription}
              onOpenObject={onOpenObject}
            />
          ) : inputData ? (
            <ToolResultRenderer data={inputData} onOpenObject={onOpenObject} />
          ) : null}
        </VStack>
      ) : null}

      {config.isStreaming && effectiveToolCallId ? (
        streamingContent ? (
          streamingContent
        ) : (
          <Box p={2} borderRadius="sm">
            <Text fontSize="xs" color="fg.muted">
              {t('streamingUnavailable')}
            </Text>
          </Box>
        )
      ) : null}

      {outputData ? (
        <VStack alignItems="start" gap={2} width="100%">
          <ToolResultRenderer data={outputData} onOpenObject={onOpenObject} />
        </VStack>
      ) : shouldShowPendingResult ? (
        <HStack py={2} alignItems="center" width="100%">
          <Box
            h="1.5"
            w="120px"
            borderRadius="full"
            animationStyle="shimmer"
            style={pendingResultStyle}
          />
        </HStack>
      ) : null}
    </>
  );
});
